<script lang="ts" setup>
import CitySelectLoading from "#/components/header/CitySelectLoading.vue";
import CitySelectNoResult from "#/components/header/CitySelectNoResult.vue";

type City = {
  id: string;
  text: string;
};

const props = defineProps<{
  cities: City[];
  query: string;
  isLoading: boolean;
  isPaginationLoading: boolean;
  hasSearched: boolean;
}>();

const emit = defineEmits<{
  (e: "update:query", value: string): void;
  (e: "select-city", city: City): void;
  (e: "scroll-dropdown", event: Event): void;
}>();

const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement;

  emit("update:query", target.value);
};

const handleSelectCity = (city: City): void => {
  emit("select-city", city);
};

const handleScroll = (event: Event): void => {
  emit("scroll-dropdown", event);
};
</script>

<template>
  <div class="city-select__mobile-wrapper">
    <div class="city-select__mobile-input-wrapper">
      <input
        :value="query"
        type="text"
        placeholder="Введите название города"
        class="city-select__input city-select__mobile-input"
        @input="handleInput"
      />

      <div class="city-select__mobile-search" />
    </div>

    <div class="city-select__mobile-content" @scroll="handleScroll">
      <div v-if="isLoading" class="city-select__loader">
        <city-select-loading />
      </div>

      <template v-else-if="cities.length">
        <div
          v-for="city in cities"
          :key="city.id"
          class="city-select__item"
          @click.prevent="handleSelectCity(city)"
        >
          {{ city.text }}
        </div>

        <div v-if="isPaginationLoading" class="city-select__loader">
          <city-select-loading />
        </div>
      </template>

      <div v-else-if="hasSearched">
        <city-select-no-result />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.city-select {
  &__input {
    width: 100%;
    background: transparent;
    font-size: 16px;

    &::placeholder {
      font-size: 16px;
    }
  }

  &__mobile {
    &-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    &-input-wrapper {
      position: relative;
      margin: 0 16px;
      flex-shrink: 0;
    }

    &-input {
      max-width: 100%;
      box-sizing: border-box;
      outline: none;
      padding: 12px 0 12px 44px;
      border: 2px solid #dedee5;
      border-radius: 16px;
      font-family: Onest, sans-serif;
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      letter-spacing: 0;
    }

    &-search {
      position: absolute;
      top: 50%;
      left: 16px;
      width: 20px;
      height: 20px;
      transform: translateY(-50%);
      background: url("../img/icons/loupe.svg") no-repeat center;
      background-size: contain;
      pointer-events: none;
      z-index: 2;
    }

    &-content {
      margin-top: 12px;
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
      max-height: calc(100dvh - 140px);
    }
  }

  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  &__item {
    display: flex;
    align-items: center;
    padding: 16px 16px;
    font-size: 16px;
    line-height: 20px;
    cursor: pointer;

    &:not(:last-child) {
      border-bottom: 1px solid #f3f4f6;
    }
  }
}
</style>
